import React, { useEffect, useState } from 'react';
import { leaveEncashmentAPI } from '../../../services';
import { Button, Card, Select, Badge, Textarea, Modal, LoadingSpinner, ErrorMessage, SuccessMessage } from '../../UI';
import { ChevronDown, ChevronUp, Check, X } from 'lucide-react';

const STATUS_BADGE = {
  pending: 'warning',
  auto_approved: 'success',
  approved: 'success',
  rejected: 'danger',
  cancelled: 'secondary',
  paid: 'primary',
};

const STATUS_LABEL = {
  pending: 'Pending',
  auto_approved: 'Auto-Approved',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  paid: 'Paid',
};

const TRIGGER_LABEL = {
  annual: 'Annual Window',
  resignation: 'Resignation / F&F',
  retirement: 'Retirement',
};

const currency = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const EncashmentRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [triggerFilter, setTriggerFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [actionId, setActionId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectComments, setRejectComments] = useState('');

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, triggerFilter]);

  const loadRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (triggerFilter) params.triggerType = triggerFilter;
      const res = await leaveEncashmentAPI.getRequests(params);
      setRequests(res.data?.data || res.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load encashment requests');
    } finally {
      setLoading(false);
    }
  };

  const pendingStage = (request) => {
    if (request.managerAction?.status === 'pending') return 'Manager';
    if (request.hrAction?.status === 'pending') return 'HR';
    return null;
  };

  const handleApprove = async (id) => {
    setActionId(id);
    setError('');
    setSuccess('');
    try {
      await leaveEncashmentAPI.approveRequest(id);
      setSuccess('Request approved');
      await loadRequests();
    } catch (err) {
      setError(err.message || 'Failed to approve request');
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectComments.trim()) return;
    setActionId(rejectTarget);
    setError('');
    setSuccess('');
    try {
      await leaveEncashmentAPI.rejectRequest(rejectTarget, rejectComments);
      setSuccess('Request rejected');
      setRejectTarget(null);
      setRejectComments('');
      await loadRequests();
    } catch (err) {
      setError(err.message || 'Failed to reject request');
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Leave Encashment Requests</h1>
        <p className="text-secondary-600">Review, approve or reject encashment requests with their full calculation ledger</p>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess('')} />}

      <Card title="Filters">
        <div className="flex flex-wrap gap-4">
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} containerClassName="w-48">
            <option value="">All</option>
            {Object.entries(STATUS_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </Select>
          <Select label="Trigger" value={triggerFilter} onChange={(e) => setTriggerFilter(e.target.value)} containerClassName="w-48">
            <option value="">All</option>
            {Object.entries(TRIGGER_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </Select>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-8"><LoadingSpinner /></div>
      ) : (
        <Card title={`Requests (${requests.length})`}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-secondary-500 border-b">
                  <th className="py-2 pr-4">Employee</th>
                  <th className="py-2 pr-4">Leave Type</th>
                  <th className="py-2 pr-4">Trigger</th>
                  <th className="py-2 pr-4">Days</th>
                  <th className="py-2 pr-4">Amount</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => {
                  const stage = pendingStage(req);
                  const isExpanded = expandedId === req._id;
                  return (
                    <React.Fragment key={req._id}>
                      <tr className="border-b last:border-0">
                        <td className="py-2 pr-4">{req.employee?.name || req.employee}</td>
                        <td className="py-2 pr-4 capitalize">{req.leaveType}</td>
                        <td className="py-2 pr-4">{TRIGGER_LABEL[req.triggerType]}</td>
                        <td className="py-2 pr-4">{req.daysRequested}</td>
                        <td className="py-2 pr-4">{currency(req.amount)}</td>
                        <td className="py-2 pr-4">
                          <Badge variant={STATUS_BADGE[req.status] || 'secondary'}>
                            {STATUS_LABEL[req.status] || req.status}
                            {stage ? ` · ${stage}` : ''}
                          </Badge>
                        </td>
                        <td className="py-2 pr-4">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="text-secondary-500 hover:text-secondary-700"
                              onClick={() => setExpandedId(isExpanded ? null : req._id)}
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            {req.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="success"
                                  onClick={() => handleApprove(req._id)}
                                  loading={actionId === req._id}
                                >
                                  <Check className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() => { setRejectTarget(req._id); setRejectComments(''); }}
                                  disabled={actionId === req._id}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-secondary-50">
                          <td colSpan={7} className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="text-sm space-y-1">
                                <div className="flex justify-between"><span>Basic</span><span>{currency(req.ledgerSnapshot?.components?.basic)}</span></div>
                                <div className="flex justify-between"><span>DA</span><span>{currency(req.ledgerSnapshot?.components?.da)}</span></div>
                                <div className="flex justify-between"><span>HRA</span><span>{currency(req.ledgerSnapshot?.components?.hra)}</span></div>
                                <div className="flex justify-between"><span>Special Allowance</span><span>{currency(req.ledgerSnapshot?.components?.special)}</span></div>
                                <div className="flex justify-between font-medium border-t pt-1"><span>Component Sum</span><span>{currency(req.ledgerSnapshot?.componentSum)}</span></div>
                                <div className="flex justify-between"><span>÷ Divisor</span><span>{req.ledgerSnapshot?.divisor}</span></div>
                                <div className="flex justify-between"><span>Per-Day Rate</span><span>{currency(req.ledgerSnapshot?.perDayRate)}</span></div>
                                <div className="flex justify-between font-bold text-primary-700 border-t pt-1"><span>Final Amount</span><span>{currency(req.ledgerSnapshot?.finalAmount)}</span></div>
                              </div>
                              <div className="text-sm">
                                <p className="font-medium text-secondary-700 mb-2">Timeline</p>
                                <ul className="space-y-1">
                                  {(req.timeline || []).map((t, i) => (
                                    <li key={i} className="text-secondary-600">
                                      {new Date(t.timestamp).toLocaleString()} — {t.action}{t.comments ? `: ${t.comments}` : ''}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {requests.length === 0 && (
            <p className="text-sm text-secondary-500 text-center py-6">No encashment requests match these filters.</p>
          )}
        </Card>
      )}

      <Modal isOpen={!!rejectTarget} onClose={() => setRejectTarget(null)} title="Reject Encashment Request">
        <Textarea
          label="Reason for rejection *"
          value={rejectComments}
          onChange={(e) => setRejectComments(e.target.value)}
          placeholder="Explain why this request is being rejected..."
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleReject} disabled={!rejectComments.trim()} loading={actionId === rejectTarget}>
            Reject Request
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default EncashmentRequestsPage;
